import _ from 'lodash';

import {
    POST_RECEIVE_WORKPOOLS_ENUM,
    TOP_PRE_RECEIVE_WORKPOOLS_ENUM
} from './WorkPools';
import {SHIPMENT_TYPES_ENUM} from './Dimensions';

export const METRIC_AGGREGATION_TYPES = Object.freeze({
    CARDINALITY: 'CARDINALITY'
});

export const METRIC_FIELD_NAMES = Object.freeze({
    CONTAINER_ID: 'ContainerIdMetricField',
    OUTER_CONTAINER_ID: 'OuterContainerIdMetricField',
    OUTER_OUTER_CONTAINER_ID: 'OuterOuterContainerIdMetricField',
    SCANNABLE_ID: 'ScannableIdMetricField',
    OUTER_SCANNABLE_ID: 'OuterScannableIdMetricField',
    OUTER_OUTER_SCANNABLE_ID: 'OuterOuterScannableIdMetricField',
    QUANTITY: 'QuantityMetricField'
});

const CONTAINER_ID_METRIC_FIELD = METRIC_FIELD_NAMES.CONTAINER_ID;
const SCANNABLE_ID_METRIC_FIELD = METRIC_FIELD_NAMES.SCANNABLE_ID;
const toWorkPoolValue = workPoolName => _.replace(workPoolName, / /g, '');

function getMetricFieldKey(workPool, shipmentType) {
    return `${workPool}:${shipmentType}`;
}

/**
 * Product configuration for the unit of work represented by each workpool and shipment type.
 * Key format: "workPool:shipmentType"
 */
export const WORK_POOL_SHIPMENT_TYPE_TO_METRIC_FIELD_MAP = new Map([
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.NOT_YET_ARRIVED, SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.NOT_YET_ARRIVED, SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.IN_YARD, SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.IN_YARD, SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.PRE_RECEIVE_DEFECT, SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.PRE_RECEIVE_DEFECT, SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.NOT_YET_RECEIVED, SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.NOT_YET_RECEIVED, SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.UNLOADED, SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(TOP_PRE_RECEIVE_WORKPOOLS_ENUM.UNLOADED, SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.TRANS_RECEIVED), SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.TRANS_RECEIVED), SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.VENDOR_RECEIVED), SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.VENDOR_RECEIVED), SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.UNLOADED_TO_CONVEYANCE), SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.UNLOADED_TO_CONVEYANCE), SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), CONTAINER_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.DECANTED), SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.DECANTED), SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.PENDING_STOW), SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.PENDING_STOW), SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.STOWED), SHIPMENT_TYPES_ENUM.TRANSSHIPMENT), SCANNABLE_ID_METRIC_FIELD],
    [getMetricFieldKey(toWorkPoolValue(POST_RECEIVE_WORKPOOLS_ENUM.STOWED), SHIPMENT_TYPES_ENUM.VENDOR_RECEIVE), SCANNABLE_ID_METRIC_FIELD]
]);

/**
 * Groups workpools by metric field for a given shipment type.
 * Returns a Map of metricFieldName -> [workPools].
 */
export function groupWorkPoolsByMetricField(workPools, shipmentType) {
    const metricFieldToWorkPoolsMap = new Map();

    workPools.forEach(workPool => {
        const key = getMetricFieldKey(workPool, shipmentType);
        const metricFieldName = WORK_POOL_SHIPMENT_TYPE_TO_METRIC_FIELD_MAP.get(key);
        if (_.isNil(metricFieldName)) {
            throw new Error(`No metric field is configured for workpool ${workPool} and shipment type ${shipmentType}`);
        }
        if (!metricFieldToWorkPoolsMap.has(metricFieldName)) {
            metricFieldToWorkPoolsMap.set(metricFieldName, []);
        }
        metricFieldToWorkPoolsMap.get(metricFieldName).push(workPool);
    });

    return metricFieldToWorkPoolsMap;
}

export function createCardinalityMetricAggregation(metricFieldName) {
    if (!_.includes(_.values(METRIC_FIELD_NAMES), metricFieldName)) {
        throw new Error(`Unsupported metric field ${metricFieldName}`);
    }
    return {
        metricField: metricFieldName,
        metricType: METRIC_AGGREGATION_TYPES.CARDINALITY
    };
}

export function createGroupByDimension(dimensionName, bucketSizeInMinutes, bucketReferencePoint) {
    const dimensionConfig = {};
    if (!_.isNil(bucketSizeInMinutes)) {
        dimensionConfig.bucketSize = bucketSizeInMinutes;
    }
    if (!_.isNil(bucketReferencePoint)) {
        dimensionConfig.bucketReferencePoint = bucketReferencePoint;
    }
    return {
        [dimensionName]: dimensionConfig
    };
}
